import { useState } from 'react'
import { Plus, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { ColorDot } from '@/components/ui/color-dot'
import { TagBadge } from './tag-badge'
import type { Tag } from '@/lib/hooks'
import { COLOR_PRESETS } from '@/lib/constants'
import { validateAndToast, createTagSchema } from '@/lib/schemas'

type TagSelectProps = {
  tags: Tag[]
  selectedTagIds: string[]
  onTagIdsChange: (ids: string[]) => void
  onCreateTag?: (data: { name: string; color: string }) => void
  lastUsedTags?: Tag[]
}

export function TagSelect({ tags, selectedTagIds, onTagIdsChange, onCreateTag, lastUsedTags }: TagSelectProps) {
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string>(COLOR_PRESETS[0])

  const selectedTags = tags.filter(t => selectedTagIds.includes(t.id))
  const lastUsedShortcuts = (lastUsedTags ?? []).filter(
    t => !selectedTagIds.includes(t.id),
  )

  const toggleTag = (tagId: string) => {
    if (selectedTagIds.includes(tagId)) {
      onTagIdsChange(selectedTagIds.filter(id => id !== tagId))
    } else {
      onTagIdsChange([...selectedTagIds, tagId])
    }
  }

  const removeTag = (tagId: string) => {
    onTagIdsChange(selectedTagIds.filter(id => id !== tagId))
  }

  const handleCreate = () => {
    if (!onCreateTag) return
    const payload = validateAndToast(createTagSchema, { name: newName, color: newColor })
    if (!payload) return
    onCreateTag(payload)
    setNewName('')
    setNewColor(COLOR_PRESETS[0])
    setCreating(false)
  }

  return (
    <div className="space-y-2">
      <Label>タグ（任意）</Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {selectedTags.map(tag => (
          <button
            key={tag.id}
            type="button"
            onClick={() => removeTag(tag.id)}
            className="group inline-flex items-center"
          >
            <TagBadge name={tag.name} color={tag.color} />
          </button>
        ))}
        <Sheet
          open={open}
          onOpenChange={(v) => { setOpen(v); if (!v) setCreating(false) }}
        >
          <SheetTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs">
              <Plus className="size-3" />
              タグを追加
            </Button>
          </SheetTrigger>
          {lastUsedShortcuts.map(tag => (
            <button
              key={tag.id}
              type="button"
              onClick={() => onTagIdsChange([...selectedTagIds, tag.id])}
              className="inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-dashed px-2.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              title="前回使ったタグ"
            >
              <Plus className="size-3 shrink-0" />
              <ColorDot color={tag.color} size="sm" />
              <span className="truncate">{tag.name}</span>
            </button>
          ))}
          <SheetContent
            side="bottom"
            className="max-h-[85vh] overflow-y-auto"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <SheetHeader>
              <SheetTitle>タグを選択</SheetTitle>
              <SheetDescription>
                この取引に紐付けるタグを選びます。新しいタグを作ることもできます。
              </SheetDescription>
            </SheetHeader>
            <div className="space-y-3 px-4 pb-4">
              {tags.length === 0 && !creating && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  タグがありません
                </p>
              )}
              {tags.map(tag => {
                const selected = selectedTagIds.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-accent active:bg-accent/80"
                    onClick={() => toggleTag(tag.id)}
                  >
                    <ColorDot color={tag.color} size="md" />
                    <span className="flex-1 text-left text-sm">{tag.name}</span>
                    {selected && <Check className="size-4 text-primary" />}
                  </button>
                )
              })}

              {creating ? (
                <div className="space-y-3 rounded-lg border p-3">
                  <Input
                    autoFocus
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="タグ名"
                    enterKeyHint="done"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newName.trim()) handleCreate()
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    {COLOR_PRESETS.map(color => (
                      <button
                        key={color}
                        type="button"
                        className={`size-9 rounded-full border-2 transition-transform hover:scale-110 ${newColor === color ? 'border-foreground' : 'border-transparent'}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setNewColor(color)}
                        aria-label={`色: ${color}`}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setCreating(false)}
                    >
                      <X className="size-4" />
                      キャンセル
                    </Button>
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={handleCreate}
                      disabled={!newName.trim()}
                    >
                      作成
                    </Button>
                  </div>
                </div>
              ) : onCreateTag ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setCreating(true)}
                >
                  <Plus className="size-4" />
                  新しいタグを作成
                </Button>
              ) : null}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  )
}
